// iter681 — Twin Leverage Card.
// Surfaces the top 3 multi-twin behaviour interventions ranked by
// gap-weighted leverage. Lets the user see how a single action ripples
// across multiple twins, with concrete projected deltas.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useTheme } from "@/theme/ThemeContext";
import { Zap, ChevronDown, ChevronUp } from "lucide-react";
import { TWIN_TOOLTIPS } from "@/lib/twinTooltips";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TWIN_LABELS = {
  nutritional: "Хранителен", cognitive: "Когнитивен", lab: "Лабораторен",
  phenotype: "Фенотипен", microbiome: "Микробиом", body_comp: "Телесен",
  sleep: "Сън", stress: "Стрес", thyroid: "Щитовидна", cardio: "Кардио",
  hormonal: "Хормонален", metabolic_insulin: "Метаболитен",
  inflammation: "Възпалителен", movement: "Движение",
  mental_emotional: "Психо", liver_kidney: "Черен дроб",
  dental: "Дентален", bhi: "BHI",
};

export default function TwinLeverageCard() {
  const { isDark } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/health-twin/leverage`, { params: { limit: 4 }, timeout: 45000 })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div
        data-testid="twin-leverage-card-loading"
        className={`p-3 rounded-xl border ${isDark ? "bg-slate-800/40 border-slate-700/40" : "bg-slate-50 border-slate-200/60"}`}
      >
        <div className="flex items-center gap-2 text-xs opacity-60">
          <Zap className="w-3.5 h-3.5 animate-pulse" />
          Изчисляваме най-полезните действия за теб...
        </div>
      </div>
    );
  }

  if (!data?.interventions?.length) return null;

  return (
    <div
      data-testid="twin-leverage-card"
      className={`p-3 rounded-xl border ${
        isDark
          ? "bg-gradient-to-br from-amber-500/10 to-emerald-500/10 border-amber-500/20"
          : "bg-gradient-to-br from-amber-50 to-emerald-50 border-amber-200/60"
      }`}
    >
      <div className="flex items-center gap-1.5 mb-2 text-[11px] uppercase font-bold opacity-70">
        <Zap className="w-3.5 h-3.5 text-amber-500" />
        Най-голям ефект днес — през няколко близнака
      </div>

      <div className="space-y-1.5">
        {data.interventions.slice(0, 4).map((iv, idx) => {
          const isExpanded = expanded === iv.id;
          return (
            <div
              key={iv.id}
              data-testid={`twin-leverage-item-${idx}`}
              className={`rounded-lg border transition-all ${
                isDark ? "bg-slate-900/40 border-slate-700/40" : "bg-white/60 border-slate-200/60"
              }`}
            >
              <button
                onClick={() => setExpanded(isExpanded ? null : iv.id)}
                className="w-full p-2 text-left flex items-center gap-2"
                data-testid={`twin-leverage-toggle-${idx}`}
              >
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                  idx === 0
                    ? "bg-amber-500 text-white"
                    : isDark
                    ? "bg-slate-700 text-slate-300"
                    : "bg-slate-200 text-slate-600"
                }`}>
                  #{idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold leading-tight truncate">{iv.title_bg}</div>
                  <div className="text-[10px] opacity-60 leading-tight">
                    +{iv.total_raw} точки през {iv.twins_count} близнаци · ефект {iv.leverage_score}
                  </div>
                </div>
                {isExpanded ? (
                  <ChevronUp className="w-3.5 h-3.5 opacity-60" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                )}
              </button>
              {isExpanded && (
                <div className={`px-2 pb-2 border-t text-[11px] ${
                  isDark ? "border-slate-700/40" : "border-slate-200/60"
                }`}>
                  <p className="opacity-70 leading-snug mt-1.5">{iv.description_bg}</p>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {iv.twins_hit.map((t) => (
                      <span
                        key={t.twin_id}
                        title={TWIN_TOOLTIPS[t.twin_id]?.title || t.twin_id}
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] ${
                          isDark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-700"
                        }`}
                      >
                        {TWIN_LABELS[t.twin_id] || t.twin_id} <span className="font-bold">+{t.delta}</span>
                      </span>
                    ))}
                  </div>
                  <p className={`text-[10px] mt-1.5 italic ${
                    isDark ? "text-slate-400" : "text-slate-500"
                  }`}>
                    📚 {iv.evidence_bg}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
